import React from "react";
import type { CSSProperties } from "react";
import { FONT_BODY, FONT_MONO } from "theme/fonts";
import { useLang } from "i18n/lang";
import { SocialNetwork, networkFromLink, socialQrText } from "./SocialQrModal.text";

const mono = FONT_MONO;

export interface SocialQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Picks the localized title/alt/hint/button text; guessed from `link` when omitted. */
  network?: SocialNetwork;
  /** English text; in other languages the network's translated text is used. */
  title?: string;
  qrSrc: string;
  qrAlt?: string;
  scanHint?: string;
  link: string;
  buttonLabel?: string;
  accent?: string;
  accentRgb?: string;
}

const SocialQrModal: React.FC<SocialQrModalProps> = ({
  isOpen,
  onClose,
  network,
  title: titleProp,
  qrSrc,
  qrAlt: qrAltProp,
  scanHint: scanHintProp,
  link,
  buttonLabel: buttonLabelProp,
  accent = "#378add",
  accentRgb = "55,138,221",
}) => {
  const { lang } = useLang();
  if (!isOpen) return null;

  // English: the caller's text as given (falling back to the preset).
  // Other languages: the translated preset for this network.
  const t = socialQrText[lang];
  const net = network ?? networkFromLink(link);
  const preset = net ? t.networks[net] : undefined;
  const useProps = lang === "en" || !preset;
  const title       = (useProps && titleProp)       || preset?.title       || titleProp || "";
  const qrAlt       = (useProps && qrAltProp)       || preset?.qrAlt       || qrAltProp || "";
  const scanHint    = (useProps && scanHintProp)    || preset?.scanHint    || scanHintProp || "";
  const buttonLabel = (useProps && buttonLabelProp) || preset?.buttonLabel || buttonLabelProp || "";

  const overlay: CSSProperties = {
    position: "fixed", inset: 0, zIndex: 10001, // above ContactModal's 10000
    background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
  };
  const modal: CSSProperties = {
    background: "#0d2235",
    border: `1px solid rgba(${accentRgb},0.28)`,
    borderRadius: 14, width: "100%", maxWidth: 320,
    boxShadow: "0 24px 60px rgba(0,0,0,0.75)",
    padding: "20px 22px 22px",
    display: "flex", flexDirection: "column", alignItems: "center",
    textAlign: "center",
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: 16 }}>
          <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: accent }}>
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label={t.closeAria}
            style={{
              background: "transparent", border: `1px solid rgba(${accentRgb},0.28)`,
              borderRadius: 6, width: 28, height: 28, flexShrink: 0,
              color: "rgba(255,255,255,0.45)", cursor: "pointer",
              fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        <div style={{ background: "#ffffff", padding: 14, borderRadius: 10 }}>
          <img src={qrSrc} alt={qrAlt} style={{ display: "block", width: 168, height: 168 }} />
        </div>

        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontFamily: mono, marginTop: 14, lineHeight: 1.6 }}>
          {scanHint}
        </div>

        <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
          <div style={{ flex: 1, height: 1, background: `rgba(${accentRgb},0.18)` }} />
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontFamily: mono, letterSpacing: 1, textTransform: "uppercase" }}>{t.or}</span>
          <div style={{ flex: 1, height: 1, background: `rgba(${accentRgb},0.18)` }} />
        </div>

        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: "100%", boxSizing: "border-box",
            background: accent, border: "none", borderRadius: 6, padding: "11px 0",
            color: "white", fontSize: 13, fontFamily: mono,
            letterSpacing: 1, textTransform: "uppercase", textAlign: "center",
            textDecoration: "none", fontWeight: 600, display: "block",
          }}
        >
          {buttonLabel}
        </a>
      </div>
    </div>
  );
};

export default SocialQrModal;
